import type { FC } from 'react';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { createTipJarTier } from '@wix/da-shared-react/pkg/Tier/redux/creator/actions';
import TierReduxModule from '@wix/da-shared-react/pkg/Tier/redux/creator/ModuleLoadable';
import { useRestrictedToCore } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictedToCore';
import {
  popModal,
  pushModal,
} from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import s from './TierCreateModal.scss';

interface Props {}

const TierCreateModal: FC<Props> = () => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const dispatchCreateTipJarTier = useAction(createTipJarTier);
  const dispatchPushModal = useAction(pushModal);
  const dispatchPopModal = useAction(popModal);

  const restrictedCreateTipJarTier = useRestrictedToCore(
    dispatchCreateTipJarTier,
    Url.studio()
  );

  const handleCreateTipJarClick = useCallback(() => {
    dispatchPopModal();
    restrictedCreateTipJarTier();
  }, [restrictedCreateTipJarTier, dispatchPopModal]);

  const handleCreateTierClick = useCallback(() => {
    dispatchPopModal();
    dispatchPushModal(ModalType.TIER_EDIT, { wrapInModal: false });
  }, [dispatchPopModal, dispatchPushModal]);

  return (
    <>
      <TierReduxModule />
      <MessageModal
        isOpen
        variant="vertical"
        onRequestClose={dispatchPopModal}
        className={s['root']}
        image={
          <img
            alt=""
            className={s['illustration']}
            src="https://st.deviantart.net/eclipse/studio/cheering-crowned-star.png"
          />
        }
        title={
          hasCoreMembership
            ? t('studio.modals.createTier.title.core')
            : t('studio.modals.createTier.title.noCore')
        }
        message={
          hasCoreMembership
            ? t('studio.modals.createTier.subtitle.core')
            : t('studio.modals.createTier.subtitle.noCore')
        }
        footerButtons={
          hasCoreMembership
            ? [
                <TextButton
                  key="tip-jar"
                  size="large"
                  variant="main"
                  onClick={handleCreateTipJarClick}
                >
                  {t('studio.modals.createTier.button.tipJar.label')}
                </TextButton>,
                <BrandedButton
                  key="create-tier"
                  variant="brand-green"
                  size="large"
                  onClick={handleCreateTierClick}
                >
                  {t('studio.modals.createTier.button.create.label')}
                </BrandedButton>,
              ]
            : [
                <TextButton
                  key="create-tier-draft"
                  variant="main"
                  size="large"
                  onClick={handleCreateTierClick}
                >
                  {t('studio.modals.createTier.button.createDraft.label')}
                </TextButton>,
                <BrandedButton
                  key="upgrade"
                  variant="core-orange"
                  size="large"
                  href={Url.coreMembershipLink({ referer: Url.studio() })}
                  target="_blank"
                >
                  {t('studio.modals.createTier.button.upgradeCore.label')}
                </BrandedButton>,
              ]
        }
      />
    </>
  );
};

TierCreateModal.displayName = 'TierCreateModal';

export default TierCreateModal;
